import React, { useContext, useEffect, useState, useMemo } from 'react';
import { Box, Tooltip } from '@mui/material';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import { CacheContext, CacheContextType } from '../context/CacheContext';
import { AuthContext, MyContext } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import { getPowerHourStatus, formatPowerHourRemaining, formatTimeUntil } from '../lib/powerHour';

/**
 * MORTALITY WEATHER — ambient widget showing the state of the time economy.
 *   🪦 N posts expiring in next 1h
 *   🧑‍🚀 M wipers online (approx — users active in last 15 min)
 *   ♾️  X% permanence rate this week
 *   ⚡ Power Hour in Yh (or "ACTIVE — 12m left")
 *
 * Refreshes every 30s. Client-side derivations from the cache to avoid
 * extra queries — except the wipers count and permanence rate, which
 * come from Supabase.
 */
function MortalityWeather() {
  const allPosts = useContext<CacheContextType>(CacheContext).allPosts;
  const { userRecord } = useContext<MyContext>(AuthContext);
  const [wipersOnline, setWipersOnline] = useState<number | null>(null);
  const [permanenceRate, setPermanenceRate] = useState<number | null>(null);
  const [tick, setTick] = useState(0);

  // Personal mortality state — derived from the current user's wallet vs
  // the next monthly wipe. If the wallet won't carry them past wipe night,
  // they get a red personal line warning them about death.
  const personalState = useMemo(() => {
    if (!userRecord) return null;
    if (userRecord.dying_at) {
      const dyingMs = new Date(userRecord.dying_at).getTime();
      const reaperMs = dyingMs + 7 * 86400 * 1000;
      const remaining = Math.max(0, Math.floor((reaperMs - Date.now()) / 1000));
      return { mode: 'dying' as const, secondsRemaining: remaining };
    }
    if (userRecord.permanence_tokens > 0) return { mode: 'safe' as const };
    const now = new Date();
    const wipeMs = Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1, 0, 0, 0);
    const secondsToWipe = Math.max(0, Math.floor((wipeMs - Date.now()) / 1000));
    if (userRecord.time_wallet_seconds <= secondsToWipe) {
      return {
        mode: 'at_risk' as const,
        walletSeconds: userRecord.time_wallet_seconds,
        wipeSeconds: secondsToWipe,
      };
    }
    return { mode: 'safe' as const };
  }, [userRecord, tick]);

  // Tick forward every 30s so Power Hour countdown updates
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  // Server-side stats: wipers online + weekly permanence rate
  useEffect(() => {
    const fetchStats = async () => {
      const fifteenMinAgo = DateTime.now().minus({ minutes: 15 }).toISO();
      const oneWeekAgo = DateTime.now().minus({ days: 7 }).toISO();

      const [wipersRes, allWeekRes, permWeekRes] = await Promise.all([
        supabase.from('posts').select('user_record_id', { count: 'exact', head: true })
          .gte('created_at', fifteenMinAgo),
        supabase.from('posts').select('id', { count: 'exact', head: true })
          .gte('created_at', oneWeekAgo),
        supabase.from('posts').select('id', { count: 'exact', head: true })
          .gte('created_at', oneWeekAgo).eq('is_permanent', true),
      ]);

      setWipersOnline(wipersRes.count ?? 0);
      const total = allWeekRes.count ?? 0;
      const perm = permWeekRes.count ?? 0;
      setPermanenceRate(total === 0 ? 0 : Math.round((perm / total) * 1000) / 10); // one decimal
    };
    fetchStats();
    const id = setInterval(fetchStats, 60_000);
    return () => clearInterval(id);
  }, []);

  // Client-side: posts dying in next hour
  const dyingCount = useMemo(() => {
    const now = DateTime.now();
    const oneHour = now.plus({ hours: 1 });
    return allPosts().filter((p) => {
      if (p.is_expired || p.is_permanent) return false;
      const exp = DateTime.fromISO(p.expire_at);
      return exp > now && exp <= oneHour;
    }).length;
  }, [allPosts, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const powerStatus = useMemo(() => getPowerHourStatus(), [tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const powerLabel = powerStatus.isActive
    ? `ACTIVE · ${formatPowerHourRemaining(powerStatus.msUntilEnd)} LEFT`
    : `in ${formatTimeUntil(powerStatus.msUntilStart)}`;

  return (
    <Box
      sx={{
        mb: 2,
        px: 1.5,
        py: 1,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)',
        fontFamily: '"VT323", monospace',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, auto)' },
          alignItems: 'center',
          justifyContent: { xs: 'space-between', sm: 'space-around' },
          gap: { xs: 0.75, sm: 2 },
          rowGap: 0.75,
        }}
      >
        <Tooltip title="Posts expiring in the next hour">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.92rem' }}>
            <span>🪦</span>
            <Box component="span" sx={{ color: dyingCount > 0 ? 'error.main' : 'text.secondary', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
              {dyingCount}
            </Box>
            <Box component="span" sx={{ color: 'text.disabled', fontSize: '0.82rem' }}>
              dying
            </Box>
          </Box>
        </Tooltip>

        <Tooltip title="Posts created in the last 15 minutes">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.92rem' }}>
            <span>🧑‍🚀</span>
            <Box component="span" sx={{ color: 'success.main', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
              {wipersOnline ?? '…'}
            </Box>
            <Box component="span" sx={{ color: 'text.disabled', fontSize: '0.82rem' }}>
              wipers
            </Box>
          </Box>
        </Tooltip>

        <Tooltip title="Fraction of posts made permanent this week">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.92rem' }}>
            <span>♾️</span>
            <Box component="span" sx={{ color: 'warning.main', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
              {permanenceRate != null ? `${permanenceRate}%` : '…'}
            </Box>
            <Box component="span" sx={{ color: 'text.disabled', fontSize: '0.82rem' }}>
              perm
            </Box>
          </Box>
        </Tooltip>

        <Tooltip title={powerStatus.isActive ? 'Power Hour is active — time costs halved' : 'Next Power Hour — all time costs will be halved for 1 hour'}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.92rem', minWidth: 0 }}>
            <span>⚡</span>
            <Box component="span" sx={{ color: powerStatus.isActive ? '#ff5722' : 'text.secondary', fontVariantNumeric: 'tabular-nums', fontWeight: 700, letterSpacing: 0.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {powerLabel}
            </Box>
          </Box>
        </Tooltip>
      </Box>

      {personalState && personalState.mode !== 'safe' && (
        <Box
          component={Link}
          to={personalState.mode === 'dying' ? '/graveyard' : '/store'}
          sx={{
            mt: 1,
            pt: 1,
            borderTop: '1px dashed',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            fontSize: '0.88rem',
            color: '#a02018',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          <span>💀</span>
          <Box component="span" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>
            {personalState.mode === 'dying'
              ? `YOU ARE DYING — confirmed death in ${Math.max(1, Math.floor(personalState.secondsRemaining / 86400))}d unless revived. Tap for graveyard.`
              : `YOU WILL NOT SURVIVE THE NEXT WIPE — wallet ${Math.floor((personalState.walletSeconds ?? 0) / 86400)}d, wipe in ${Math.floor((personalState.wipeSeconds ?? 0) / 86400)}d. Tap for store.`}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default MortalityWeather;
